/**
 * Provider stack. Ordering matters:
 *   QueryClient → Intl → MUI theme → Keycloak → FeatureFlags → Router
 *
 * - QueryClient is outermost so every downstream hook shares the cache.
 * - Intl wraps MUI so formatted messages render inside MUI components.
 * - Keycloak sits above FeatureFlags because `/feature-flags` needs
 *   an auth'd token.
 */

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// Point at `AdapterDateFnsV3` — it re-exports `AdapterDateFns`
// targeting date-fns v3 (which has a different internals layout).
// The default `AdapterDateFns` entry still targets v2 and fails
// at bundle time: missing `_lib/format/longFormatters` in v3's
// exports map.
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ja, enUS, ptBR } from 'date-fns/locale';
import { Suspense, type ReactNode } from 'react';
import { RouterProvider } from 'react-router-dom';

import { AuthProvider } from '@/auth/AuthContext';
import { E2EAuthProvider } from '@/auth/E2EAuthProvider';
import { KeycloakProvider } from '@/auth/KeycloakProvider';
import { FeatureFlagProvider } from '@/config/feature-flags';
import { IntlProvider, useLocaleSwitch } from '@/i18n/IntlProvider';
import { router } from '@/routes';
import { muiTheme } from '@/theme/muiTheme';

// `VITE_E2E_MODE=true` swaps Keycloak for a test-only auth provider
// that reads the bearer from localStorage. Static import means Vite
// tree-shakes the branch it doesn't pick — E2EAuthProvider is out of
// production bundles automatically when `VITE_E2E_MODE` is unset.
const AuthBootstrap: React.ComponentType<{ children: React.ReactNode }> =
  import.meta.env.VITE_E2E_MODE === 'true' ? E2EAuthProvider : KeycloakProvider;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const DATE_FNS_LOCALE = { en: enUS, 'pt-BR': ptBR, ja } as const;

function LocalizedDatePickers({ children }: { children: ReactNode }) {
  const { locale } = useLocaleSwitch();
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={DATE_FNS_LOCALE[locale]}>
      {children}
    </LocalizationProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <LocalizedDatePickers>
            <AuthBootstrap>
              <AuthProvider>
                <FeatureFlagProvider>
                  <Suspense fallback={null}>
                    <RouterProvider router={router} />
                  </Suspense>
                </FeatureFlagProvider>
              </AuthProvider>
            </AuthBootstrap>
          </LocalizedDatePickers>
        </ThemeProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
}
